import { AsyncPipe, NgClass, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { FocusRemoveDirective } from '@shared/directives';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import {
  DetailsItemComponent,
  DetailsPageHeaderComponent,
  DetailsPageHeaderPlaceholderComponent,
  DetailsPageContentPlaceholderComponent,
  SuccessModalComponent,
} from '@shared/components';
import { DetailsItemIdComponent } from '@shared/components/details-item-id/details-item-id.component';
import { Breadcrumb, FundOnHold } from '@shared/models';
import { AmountPipe } from '@shared/pipes/amount.pipe';
import { DateFormatPipe, TeamMemberNamePipe } from '@shared/pipes';
import { FundsOnHoldService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';
import { Store } from '@ngrx/store';
import { FinancialAcccountActions, TeamMembersActions, financialAccountFeature } from '@store';
import { compact } from 'lodash-es';
import { BehaviorSubject, Subject, filter, take, takeUntil } from 'rxjs';
import { UpdateFundOnHoldComponent } from '../update-fund-on-hold/update-fund-on-hold.component';

@Component({
  selector: 'app-fund-on-hold-details',
  imports: [
    AsyncPipe,
    NgClass,
    TitleCasePipe,
    RouterLink,
    RktIconComponent,
    FocusRemoveDirective,
    DetailsItemComponent,
    DetailsItemIdComponent,
    DetailsPageHeaderComponent,
    DetailsPageHeaderPlaceholderComponent,
    DetailsPageContentPlaceholderComponent,
    AmountPipe,
    DateFormatPipe,
    TeamMemberNamePipe,
  ],
  templateUrl: './fund-on-hold-details.component.html',
  styleUrls: ['./fund-on-hold-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundOnHoldDetailsComponent implements OnInit, OnDestroy {
  private fundsOnHoldService = inject(FundsOnHoldService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private modalService = inject(ModalService);
  private store = inject(Store);

  fundOnHold$ = new BehaviorSubject<FundOnHold | undefined>(undefined);
  financialAccount$ = this.store.select(financialAccountFeature.selectFinancialAccountDetails);

  pageReturnURL = '';
  financialAccountUrl = '';
  releasedTransactionUrl = '';
  breadcrumbList: Breadcrumb[] = [];

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadFundOnHoldDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFundOnHoldDetails(): void {
    this.fundOnHold$.next(undefined);

    const financialAccountId = this.activatedRoute.snapshot.params['financialAccountId'];
    const holdId = this.activatedRoute.snapshot.params['holdId'];
    const businessAccountId = this.activatedRoute.snapshot.params['businessAccountId'];

    if (businessAccountId) {
      this.pageReturnURL = `/app/business-accounts/${businessAccountId}/financial-accounts/${financialAccountId}/funds-on-hold`;
      this.financialAccountUrl = `/app/business-accounts/${businessAccountId}/financial-accounts/${financialAccountId}`;

      this.breadcrumbList = [
        { label: 'Business Accounts', url: '/app/business-accounts' },
        { label: 'Business Account Details', url: `/app/business-accounts/${businessAccountId}` },
        { label: 'Financial Accounts', url: `/app/business-accounts/${businessAccountId}/financial-accounts` },
        { label: 'Financial Account Details', url: this.financialAccountUrl },
        { label: 'Funds on Hold', url: this.pageReturnURL },
        { label: 'Funds on Hold Details' },
      ];
    } else {
      this.pageReturnURL = `/app/financial-accounts/${financialAccountId}/funds-on-hold`;
      this.financialAccountUrl = `/app/financial-accounts/${financialAccountId}`;

      this.breadcrumbList = [
        { label: 'Financial Accounts', url: '/app/financial-accounts' },
        { label: 'Financial Account Details', url: this.financialAccountUrl },
        { label: 'Funds on Hold', url: this.pageReturnURL },
        { label: 'Funds on Hold Details' },
      ];
    }

    this.store.dispatch(FinancialAcccountActions.loadFinancialAccountDetails());

    this.fundsOnHoldService
      .getFundOnHoldDetails(holdId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.releasedTransactionId) {
            this.releasedTransactionUrl = `/app/transactions/${response.releasedTransactionId}/details`;
          }

          const teamMemberIds = compact([response.createdBy?.id, response.updatedBy?.id]);
          if (teamMemberIds.length) {
            this.store
              .select(financialAccountFeature.selectFinancialAccountDetails)
              .pipe(filter(Boolean), take(1), takeUntil(this.destroy$))
              .subscribe((fa) => {
                this.store.dispatch(TeamMembersActions.loadTeamMembers({ ids: teamMemberIds, businessAccountId: fa.businessAccountId }));
              });
          }

          this.fundOnHold$.next(response);
        },
        error: (error) => {
          ErrorUtils.catchError('fundsOnHoldService.getFundOnHoldDetails error', error);
          this.router.navigate(['..'], { relativeTo: this.activatedRoute });
        },
      });
  }

  onEditBtnClick(): void {
    const fundOnHold = this.fundOnHold$.value;
    if (!fundOnHold) {
      return;
    }

    const modalRef = this.modalService.open(UpdateFundOnHoldComponent, {
      className: 'entity-form-modal',
    });

    modalRef.componentInstance.modalInitData(fundOnHold);

    modalRef.result.then(
      (result) => {
        if (result) {
          const successModalRef = this.modalService.open(SuccessModalComponent, {
            className: 'confirm-modal',
          });
          successModalRef.componentInstance.customTitle = 'Fund On Hold updated successfully';

          this.loadFundOnHoldDetails();
        }
      },
      () => false,
    );
  }
}
